<template>
  <div class="">
    <div class="flex justify-between items-center q-mb-lg">
      <h3>Alla konserter</h3>
      <div class="text-caption text-grey-7">
        {{ concerts.length }} träffar
      </div>
    </div>

    <!-- Filter-rad -->
    <div class="filters q-mb-md">
      <q-input
        v-model="search"
        label="Sök på titel eller plats"
        outlined
        dense
        clearable
        debounce="300"
        @update:model-value="fetchConcerts"
        class="filter-search"
      />
      <q-select
        v-model="city"
        :options="cityOptions"
        label="Stad"
        outlined
        dense
        emit-value
        map-options
        @update:model-value="fetchConcerts"
        class="filter-city"
      />
      <q-toggle
        v-model="includeInactive"
        label="Visa inaktiva"
        @update:model-value="fetchConcerts"
      />
      <q-toggle
        v-model="includePast"
        label="Visa passerade"
        @update:model-value="fetchConcerts"
      />
    </div>

    <!-- Tabell -->
    <q-table
      :rows="concerts"
      :columns="columns"
      row-key="_id"
      :loading="loading"
      :pagination="{ rowsPerPage: 25 }"
      class="concerts-table"
    >
      <template v-slot:body-cell-imageUrl="props">
        <q-td :props="props">
          <q-img
            v-if="props.row.imageUrl"
            :src="props.row.imageUrl"
            style="width: 60px; height: 60px; border-radius: 4px"
          />
        </q-td>
      </template>

      <template v-slot:body-cell-date="props">
        <q-td :props="props">
          {{ formatDate(props.row.date) }}
        </q-td>
      </template>

      <template v-slot:body-cell-genre="props">
        <q-td :props="props">
          <q-badge v-if="props.row.genre" color="dark">
            {{ props.row.genre }}
          </q-badge>
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-highlighted="props">
        <q-td :props="props" class="text-center">
          <q-icon
            v-if="props.row.highlighted"
            name="star"
            color="amber"
            size="sm"
          />
          <q-icon v-else name="star_border" color="grey-5" size="sm" />
        </q-td>
      </template>

      <template v-slot:body-cell-isActive="props">
        <q-td :props="props" class="text-center">
          <q-badge :color="props.row.isActive ? 'green' : 'grey-6'">
            {{ props.row.isActive ? "Aktiv" : "Inaktiv" }}
          </q-badge>
        </q-td>
      </template>

      <template v-slot:body-cell-edit="props">
        <q-td :props="props" class="text-center">
          <q-btn
            icon="edit"
            color="primary"
            flat
            dense
            @click="openEdit(props.row)"
          />
        </q-td>
      </template>
    </q-table>

    <!-- Edit-dialog -->
    <q-dialog v-model="isEditOpen" persistent>
      <q-card style="width: 600px; max-width: 90vw">
        <q-card-section>
          <div class="text-h6">Redigera konsert</div>
          <div v-if="editing" class="text-caption text-grey-7 q-mt-xs">
            {{ editing.venue }} · {{ formatDate(editing.date) }}
          </div>
        </q-card-section>

        <q-card-section>
          <q-form @submit.prevent="saveEdit">
            <q-input
              v-model="form.title"
              label="Titel"
              outlined
              class="q-mb-md"
            />

            <q-select
              v-model="form.genre"
              :options="genreOptions"
              label="Genre"
              outlined
              clearable
              emit-value
              map-options
              class="q-mb-md"
            />

            <q-checkbox
              v-model="form.highlighted"
              label="Highlighted (visas i karusellen överst på sidan)"
              class="q-mb-md"
            />

            <q-checkbox
              v-model="form.isActive"
              label="Aktiv (visas i listan)"
              class="q-mb-md"
            />

            <div class="flex justify-end q-gutter-sm q-mt-md">
              <q-btn flat label="Avbryt" color="grey-7" @click="closeEdit" />
              <q-btn
                label="Spara"
                type="submit"
                color="primary"
                :loading="saving"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import { useQuasar } from "quasar";
import axios from "axios";
import { useRouter } from "vue-router";
import { clearAuth } from "@/utils/auth";

export default {
  name: "AdminConcerts",
  setup() {
    const $q = useQuasar();
    const router = useRouter();

    const concerts = ref([]);
    const genreOptions = ref([]);
    const loading = ref(false);
    const saving = ref(false);

    // Filter
    const search = ref("");
    const city = ref("Göteborg");
    const includeInactive = ref(false);
    const includePast = ref(false);

    const cityOptions = [
      { label: "Göteborg", value: "Göteborg" },
      { label: "Stockholm", value: "Stockholm" },
    ];

    // Edit-dialog state
    const isEditOpen = ref(false);
    const editing = ref(null);
    const form = ref({
      title: "",
      genre: null,
      highlighted: false,
      isActive: true,
    });

    const columns = [
      { name: "imageUrl", label: "", align: "center" },
      { name: "title", label: "Titel", field: "title", align: "left", sortable: true },
      { name: "venue", label: "Plats", field: "venue", align: "left", sortable: true },
      { name: "date", label: "Datum", field: "date", align: "left", sortable: true },
      { name: "genre", label: "Genre", align: "center" },
      { name: "highlighted", label: "★", align: "center" },
      { name: "isActive", label: "Status", align: "center" },
      { name: "edit", label: "", align: "center" },
    ];

    const formatDate = (d) => {
      if (!d) return "";
      try {
        return new Date(d).toLocaleDateString("sv-SE", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (e) {
        return d;
      }
    };

    const handleAuthError = (err) => {
      const status = err.response?.status;
      if (status === 401) {
        $q.notify({ type: "negative", message: "Logga in igen." });
        clearAuth();
        router.push("/login");
        return true;
      }
      if (status === 403) {
        $q.notify({
          type: "negative",
          message: "Du har inte behörighet för den här vyn.",
        });
        router.push("/admin");
        return true;
      }
      return false;
    };

    const fetchGenres = async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "admin/genres",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        genreOptions.value = data.map((g) => ({ label: g, value: g }));
      } catch (err) {
        if (!handleAuthError(err)) {
          $q.notify({
            type: "negative",
            message: "Kunde inte ladda genrer",
          });
        }
      }
    };

    const fetchConcerts = async () => {
      loading.value = true;
      try {
        const token = localStorage.getItem("token");
        const params = {
          city: city.value,
          search: search.value || undefined,
          includeInactive: includeInactive.value || undefined,
          includePast: includePast.value || undefined,
          limit: 500,
        };
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "admin/concerts",
          {
            headers: { Authorization: `Bearer ${token}` },
            params,
          }
        );
        concerts.value = data;
      } catch (err) {
        if (!handleAuthError(err)) {
          $q.notify({
            type: "negative",
            message: "Kunde inte hämta konserter",
          });
        }
      } finally {
        loading.value = false;
      }
    };

    const openEdit = (row) => {
      editing.value = row;
      form.value = {
        title: row.title || "",
        genre: row.genre || null,
        highlighted: !!row.highlighted,
        isActive: !!row.isActive,
      };
      isEditOpen.value = true;
    };

    const closeEdit = () => {
      isEditOpen.value = false;
      editing.value = null;
    };

    const saveEdit = async () => {
      if (!editing.value) return;
      saving.value = true;
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.patch(
          process.env.VUE_APP_API_URL +
            "admin/concerts/" +
            editing.value._id,
          {
            title: form.value.title,
            genre: form.value.genre || null,
            highlighted: form.value.highlighted,
            isActive: form.value.isActive,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Uppdatera raden lokalt så vi slipper full re-fetch
        const idx = concerts.value.findIndex((c) => c._id === data._id);
        if (idx !== -1) concerts.value[idx] = data;

        $q.notify({ type: "positive", message: "Sparat!" });
        closeEdit();
      } catch (err) {
        if (!handleAuthError(err)) {
          $q.notify({
            type: "negative",
            message:
              err.response?.data?.message || "Kunde inte spara",
          });
        }
      } finally {
        saving.value = false;
      }
    };

    onMounted(() => {
      fetchGenres();
      fetchConcerts();
    });

    return {
      concerts,
      genreOptions,
      loading,
      saving,
      search,
      city,
      cityOptions,
      includeInactive,
      includePast,
      columns,
      isEditOpen,
      editing,
      form,
      formatDate,
      fetchConcerts,
      openEdit,
      closeEdit,
      saveEdit,
    };
  },
};
</script>

<style scoped>
.filters {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.filter-search {
  min-width: 280px;
  flex: 1;
}

.filter-city {
  min-width: 160px;
}

.concerts-table :deep(thead th) {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
}
</style>
